import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSend, FiCheckCircle, FiUser, FiTarget, FiClock, FiLayers, FiMail, FiChevronRight, FiChevronLeft } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import SafeIcon from '../common/SafeIcon';

const SchedulingForm = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    nivel: '',
    objetivo: '',
    tiempo: '',
    enfoque: '',
    email: ''
  });

  const whatsappNumber = "529844594738";

  const handleNext = () => setStep(prev => prev + 1);
  const handleBack = () => setStep(prev => prev - 1);

  const handleSubmit = () => {
    const message = `*NUEVA SOLICITUD DE ACTIVACIÓN*%0A%0A` +
      `*1. Nivel Actual:* ${formData.nivel}%0A` +
      `*2. Objetivo Principal:* ${formData.objetivo}%0A` +
      `*3. Tiempo Semanal:* ${formData.tiempo}%0A` +
      `*4. Áreas de Enfoque:* ${formData.enfoque}%0A` +
      `*5. Correo:* ${formData.email}%0A%0A` +
      `_Enviado desde el Formulario de Registro OCE_`;
    
    window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
  };

  const steps = [
    {
      id: 1,
      question: "¿Cuál es tu nivel actual en el idioma que quieres aprender?",
      field: 'nivel',
      options: ['Principiante (A1-A2)', 'Intermedio (B1-B2)', 'Avanzado (C1-C2)', 'No lo sé (Necesito diagnóstico)'],
      icon: FiLayers
    },
    {
      id: 2,
      question: "¿Cuál es tu objetivo principal para aprender este idioma?",
      field: 'objetivo',
      options: ['Mejor empleo / Ascenso', 'Viajes / Mudanza', 'Examen (IELTS/TOEFL)', 'Hobby / Crecimiento Personal'],
      icon: FiTarget
    },
    {
      id: 3,
      question: "¿Cuánto tiempo puedes dedicar a aprender cada semana?",
      field: 'tiempo',
      options: ['1-3 horas', '4-7 horas', '8-12 horas', 'Más de 12 horas'],
      icon: FiClock
    },
    {
      id: 4,
      question: "¿En qué áreas quieres enfocarte más?",
      field: 'enfoque',
      options: ['Conversación / Fluidez', 'Gramática / Escritura', 'Pronunciación / Acento', 'Inglés Técnico / Negocios'],
      icon: FiUser
    }
  ];

  return (
    <div className="w-full bg-zinc-900 border-2 border-yellow-500/30 rounded-[2.5rem] p-6 md:p-10 shadow-2xl relative overflow-hidden">
      <div className="absolute top-0 right-0 p-10 bg-yellow-500/5 blur-[100px] pointer-events-none"></div>
      
      <div className="relative z-10">
        <div className="flex justify-between items-center mb-8">
          <div>
            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-yellow-500">Paso 2: Activación</span>
            <h3 className="text-2xl font-black text-white uppercase italic">Configura tu Perfil</h3>
          </div>
          <div className="text-right">
            <span className="text-2xl font-black text-zinc-700 italic">{step}/5</span>
          </div>
        </div>

        <div className="w-full h-1 bg-zinc-800 rounded-full mb-10 overflow-hidden">
          <motion.div 
            className="h-full bg-gradient-to-r from-yellow-500 to-green-500"
            initial={{ width: 0 }}
            animate={{ width: `${(step / 5) * 100}%` }}
          />
        </div>

        <AnimatePresence mode="wait">
          {step <= 4 ? (
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-zinc-800 rounded-2xl flex items-center justify-center text-yellow-500 border border-zinc-700">
                  <SafeIcon icon={steps[step-1].icon} className="text-xl" />
                </div>
                <h4 className="text-lg font-bold text-white leading-tight">{steps[step-1].question}</h4>
              </div>

              <div className="grid gap-3">
                {steps[step-1].options.map((opt) => (
                  <button
                    key={opt}
                    onClick={() => {
                      setFormData({ ...formData, [steps[step-1].field]: opt });
                      handleNext();
                    }}
                    className={`w-full text-left p-5 rounded-2xl border-2 transition-all font-bold text-sm flex justify-between items-center group ${
                      formData[steps[step-1].field] === opt 
                      ? 'border-yellow-500 bg-yellow-500/10 text-white' 
                      : 'border-zinc-800 bg-black/40 text-gray-400 hover:border-zinc-600'
                    }`}
                  >
                    {opt}
                    <FiChevronRight className={`transition-transform ${formData[steps[step-1].field] === opt ? 'translate-x-0 opacity-100' : '-translate-x-2 opacity-0 group-hover:opacity-100 group-hover:translate-x-0'}`} />
                  </button>
                ))}
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="space-y-8"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-zinc-800 rounded-2xl flex items-center justify-center text-blue-500 border border-zinc-700">
                  <SafeIcon icon={FiMail} className="text-xl" />
                </div>
                <h4 className="text-lg font-bold text-white leading-tight">¿Cuál es tu correo electrónico?</h4>
              </div>

              <input 
                type="email" 
                placeholder="ejemplo@correo.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full bg-black border-2 border-zinc-800 p-6 rounded-2xl text-white focus:border-green-500 outline-none text-lg font-bold transition-all"
              />

              <div className="bg-green-500/10 border border-green-500/30 p-6 rounded-3xl">
                <p className="text-green-500 text-xs font-bold uppercase tracking-widest mb-2 flex items-center gap-2">
                  <SafeIcon icon={FiCheckCircle} /> Todo listo para enviar
                </p>
                <p className="text-gray-400 text-sm">Al hacer clic en el botón, se abrirá tu WhatsApp con la información lista para ser procesada por un mentor.</p>
              </div>

              <button 
                onClick={handleSubmit}
                disabled={!formData.email.includes('@')}
                className="w-full bg-green-600 text-white py-6 rounded-2xl font-black uppercase tracking-widest text-lg flex items-center justify-center gap-3 hover:bg-green-500 transition-all shadow-2xl disabled:opacity-50 disabled:grayscale"
              >
                <SafeIcon icon={FaWhatsapp} className="text-2xl" /> Finalizar en WhatsApp
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {step > 1 && (
          <button 
            onClick={handleBack}
            className="mt-8 text-gray-500 hover:text-white font-bold text-xs uppercase tracking-widest flex items-center gap-2 transition-colors"
          >
            <FiChevronLeft /> Atrás
          </button>
        )}
      </div>
    </div>
  );
};

export default SchedulingForm;